import React, { useEffect, useState } from "react";
import styled from "styled-components";
import api from "../api";

const Page = styled.div`
  background: #f8fafc;
  min-height: 100vh;
  padding: 16px;
  color: #0f172a;
`;
const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 1.6rem;
  font-weight: 800;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  padding: 14px;
  margin-bottom: 12px;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;
const Field = styled.div``;
const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.8rem;
  color: #334155;
  font-weight: 700;
`;
const Input = styled.input`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Select = styled.select`
  width: 100%;
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  color: #0f172a;
  background: #fff;
`;
const Btn = styled.button`
  min-height: 44px;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? "#e2e8f0" : "#2563eb")};
  color: ${(p) => (p.$secondary ? "#0f172a" : "#fff")};
`;
const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;
const ErrorBox = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
  font-size: 0.86rem;
`;
const SuccessBox = styled.div`
  margin-bottom: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  font-weight: 700;
  font-size: 0.86rem;
`;
const ModalBg = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1200;
`;
const Modal = styled.div`
  width: 100%;
  max-width: 500px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 22px 46px rgba(2, 6, 23, 0.25);
  padding: 14px;
`;

export default function SystemConfiguration() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [form, setForm] = useState({
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: "",
    smtp_use_tls: true,
    qc_sla_minutes: 240,
    low_stock_alert_enabled: true,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/admin/system-config");
      setForm((prev) => ({ ...prev, ...(res?.data?.data || {}) }));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load system configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await api.put("/admin/system-config", form);
      setSuccess("System configuration saved.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save system configuration.");
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    setTesting(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.post("/admin/test-email", { test_to_email: testEmail });
      setSuccess(res?.data?.message || "Test email sent.");
      setShowTestModal(false);
      setTestEmail("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send test email.");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Page>
      <Title>System Configuration</Title>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {success ? <SuccessBox>{success}</SuccessBox> : null}

      <Card>
        <h4 style={{ marginTop: 0, marginBottom: 12 }}>SMTP Settings</h4>
        {loading ? (
          <div style={{ color: "#64748b" }}>Loading...</div>
        ) : (
          <Grid>
            <Field>
              <Label>SMTP Host</Label>
              <Input value={form.smtp_host} onChange={(e) => setForm((f) => ({ ...f, smtp_host: e.target.value }))} />
            </Field>
            <Field>
              <Label>SMTP Port</Label>
              <Input
                type="number"
                value={form.smtp_port}
                onChange={(e) => setForm((f) => ({ ...f, smtp_port: Number(e.target.value || 587) }))}
              />
            </Field>
            <Field>
              <Label>SMTP User</Label>
              <Input value={form.smtp_user} onChange={(e) => setForm((f) => ({ ...f, smtp_user: e.target.value }))} />
            </Field>
            <Field>
              <Label>SMTP Password</Label>
              <Input
                type="password"
                value={form.smtp_password}
                onChange={(e) => setForm((f) => ({ ...f, smtp_password: e.target.value }))}
                placeholder="Leave as is to keep existing password"
              />
            </Field>
            <Field>
              <Label>From Email</Label>
              <Input value={form.smtp_from_email} onChange={(e) => setForm((f) => ({ ...f, smtp_from_email: e.target.value }))} />
            </Field>
            <Field>
              <Label>TLS</Label>
              <Select
                value={form.smtp_use_tls ? "true" : "false"}
                onChange={(e) => setForm((f) => ({ ...f, smtp_use_tls: e.target.value === "true" }))}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </Select>
            </Field>
          </Grid>
        )}
      </Card>

      <Card>
        <h4 style={{ marginTop: 0, marginBottom: 12 }}>Thresholds</h4>
        {loading ? (
          <div style={{ color: "#64748b" }}>Loading...</div>
        ) : (
          <Grid>
            <Field>
              <Label>QC SLA Minutes</Label>
              <Input
                type="number"
                value={form.qc_sla_minutes}
                onChange={(e) => setForm((f) => ({ ...f, qc_sla_minutes: Number(e.target.value || 240) }))}
              />
            </Field>
            <Field>
              <Label>Low Stock Alert Enabled</Label>
              <Select
                value={form.low_stock_alert_enabled ? "true" : "false"}
                onChange={(e) => setForm((f) => ({ ...f, low_stock_alert_enabled: e.target.value === "true" }))}
              >
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </Select>
            </Field>
          </Grid>
        )}
      </Card>

      <Row>
        <Btn onClick={save} disabled={saving || loading}>
          {saving ? "Saving..." : "Save All"}
        </Btn>
        <Btn $secondary onClick={() => setShowTestModal(true)} disabled={loading}>
          Test Email
        </Btn>
      </Row>

      {showTestModal ? (
        <ModalBg onClick={() => setShowTestModal(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <h4 style={{ marginTop: 0 }}>Send Test Email</h4>
            <Field>
              <Label>Recipient Email</Label>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </Field>
            <Row style={{ marginTop: 12 }}>
              <Btn $secondary onClick={() => setShowTestModal(false)}>
                Cancel
              </Btn>
              <Btn onClick={sendTestEmail} disabled={testing || !testEmail}>
                {testing ? "Sending..." : "Send"}
              </Btn>
            </Row>
          </Modal>
        </ModalBg>
      ) : null}
    </Page>
  );
}
